// CX Public URL helpers — single source of truth for the public guest URL.
//
// EVERY admin-side display of a feedback URL must use `buildFeedbackPublicUrl`
// from this module so a regression that produces malformed strings like
// "hosttoken" (no slash, no /feedback/) is impossible by construction.
//
// Mirrors the backend `_feedback_url` (routes/cx_forms.py) exactly.

/**
 * Build a canonical public feedback URL.
 *
 * Defensive normalization (matches backend):
 *   • Strips whitespace + trailing slashes from base.
 *   • Strips whitespace + leading slashes from token.
 *   • Always injects literal `/feedback/` between them.
 *
 * @param {string} baseUrl  Tenant's CX base URL (e.g. "https://hotel.com").
 * @param {string} token    The area's qr_token.
 * @returns {string}        e.g. "https://hotel.com/feedback/abc123"
 */
export function buildFeedbackPublicUrl(baseUrl, token) {
  const b = String(baseUrl || '').trim().replace(/\/+$/, '');
  const t = String(token || '').trim().replace(/^\/+/, '');
  return `${b}/feedback/${t}`;
}

/**
 * Validator — mirrors backend `_is_valid_feedback_url` exactly.
 * Rejects malformed concatenations like "hosttoken" (no slash) or anything
 * missing the http(s):// scheme or `/feedback/` path segment.
 *
 * @param {string|null|undefined} url
 * @returns {boolean}
 */
export function isValidFeedbackUrl(url) {
  if (!url || typeof url !== 'string') return false;
  // ^https?://host(:port)?(/path)?/feedback/<token>$
  // Token: [A-Za-z0-9_-]+ (matches our qr_token generator).
  return /^https?:\/\/[^/\s]+(?::\d+)?(?:\/[^\s]*)?\/feedback\/[A-Za-z0-9_-]+$/.test(url.trim());
}
